import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap-5';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import snakecaseKeys from 'snakecase-keys';
import classNames from 'classnames';

import FilterSidebar from 'components/search_sidebar/filter_sidebar';
import LoadingSpinner from 'components/ui/loading_spinner';
import MobileTaskForm from 'components/tasks/mobile_task_form';
import PageHeader from 'components/ui/page_header';
import TaskForm from 'components/tasks/task_form';
import TaskList from 'components/tasks/task_list';

import Filters from './filters';
import MobileFilterBar from './mobile_filter_topbar';
import MobileTaskFilters from './mobile_task_filters';
import ProjectsView from './projects_view';

import { isMobile } from 'helpers/mobile_helpers';
import { VIEW_TASKS_LIST, VIEW_TASKS_PROJECTS } from 'helpers/tasks/constants';
import { nextTaskPage, retrieveTasks, downloadTasksAsCsv } from './store/actions/search';
import { changeTasksView } from './store/actions/ui';
import { addTask, deleteTask, updateTask, toggleTaskState } from './store/actions/tasks';
import { changeStatusFilter } from './store/actions/query';

import './tasks.scss';

export class Tasks extends React.Component {
  static propTypes = {
    activeView: PropTypes.oneOf([VIEW_TASKS_LIST, VIEW_TASKS_PROJECTS]).isRequired,
    addTask: PropTypes.func.isRequired,
    availableDatabases: PropTypes.arrayOf(PropTypes.array),
    callListsAccess: PropTypes.bool,
    currentUser: PropTypes.shape({
      id: PropTypes.number.isRequired,
      marketingListingsAccess: PropTypes.bool,
      taskAssociationsEnabled: PropTypes.bool
    }).isRequired,
    databaseMembers: PropTypes.array.isRequired,
    downloadTasksAsCsv: PropTypes.func.isRequired,
    hasNextPage: PropTypes.bool,
    isFetching: PropTypes.bool,
    retrieveTasks: PropTypes.func.isRequired,
    selectedId: PropTypes.number,
    status: PropTypes.string,
    tasks: PropTypes.array.isRequired,
    onChangeStatus: PropTypes.func.isRequired,
    onDeleteTask: PropTypes.func.isRequired,
    onNextPage: PropTypes.func.isRequired,
    onToggleTaskState: PropTypes.func.isRequired,
    onUpdateTask: PropTypes.func.isRequired,
    onViewChange: PropTypes.func.isRequired
  };

  state = {
    showTaskForm: false,
    displayMobileFilters: false,
    taskFormErrors: null
  };

  componentDidMount() {
    if (typeof ($) != 'undefined') {
      // Prevent Bootstrap vanilla JS from interfering with toggle buttons
      // https://github.com/react-bootstrap/react-bootstrap/issues/2774
      $(document).off('click.bs.button.data-api', '[data-toggle^="button"]');
    }

    this.props.retrieveTasks();
  }

  handleStatusSelect = (value, view) => {
    const { onChangeStatus, retrieveTasks, onViewChange } = this.props;

    onChangeStatus(value);
    retrieveTasks();
    onViewChange(view);
  }

  handleTaskFormClose = () => {
    this.setState({
      showTaskForm: false,
      taskFormErrors: null
    });
  }

  handleTaskFormOpen = (e) => {
    e.preventDefault();
    this.setState({
      showTaskForm: true
    });
  }

  handleTaskSave = (task) => {
    const { addTask } = this.props;
    addTask(
      task,
      this.handleTaskFormClose,
      errors => this.setState({ taskFormErrors: errors })
    );
  }

  handleDisplayMobileFilters = () => this.setState({ displayMobileFilters: true });
  handleCloseMobileFilters = () => this.setState({ displayMobileFilters: false });

  render() {
    const { isFetching } = this.props;
    return (
      <div className="contacts-tasks-index d-flex flex-column h-100">
        {this.renderPageHeader()}
        <div className="tasks-page d-flex overflow-y-hidden justify-content-center flex-grow-1 flex-shrink-1">
          {this.renderFilterSideBar()}
          {this.renderMobileFilterTopBar()}
          <div className="results-tab-container tasks-container px-5 py-7 overflow-y-scroll">
            <Filters
              assigneeLabel="Assignee"
              creatorLabel="Creator"
              hideLabels
              hideStatus
              name="task-filters"
              onDisplayFilters={this.handleDisplayMobileFilters}
            />
            {isFetching ?
              <LoadingSpinner />
              : this.renderActiveView()
            }
            {this.state.displayMobileFilters && this.renderMobileFilters()}
            {isMobile() ? this.renderMobileTaskForm() : this.renderCreateTaskModal()}
          </div>
        </div>
      </div>
    );
  }

  renderCSVButton() {
    return (
      <button className="btn btn-secondary d-none d-md-block" onClick={this.props.downloadTasksAsCsv}>
        Export as CSV
      </button>
    );
  }

  renderMobileFilterTopBar() {
    return (
      <MobileFilterBar
        onDisplayFilters={this.handleDisplayMobileFilters}
      />
    );
  }

  renderMobileFilters() {
    return (
      <div className="tasks-index-mobile-content tasks-mobile-content p-4 d-flex flex-column gap-2">
        <MobileTaskFilters
          onCloseFilters={this.handleCloseMobileFilters}
        />
      </div>
    );
  }

  renderFilterSideBar() {
    const { status, currentUser } = this.props;

    const activeButton = classNames({
      'btn': true,
      'tasks-btn w-100 d-block text-start ps-2': true,
      'active': true
    });

    const inactiveButton = classNames({
      'btn': true,
      'tasks-btn w-100 d-block text-start ps-2': true,
      'active': false
    });

    return (
      <FilterSidebar visible>
        <div>
          <div
            className={status == null ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect(null, VIEW_TASKS_LIST)}
          >
            Open
          </div>
          <div
            className={status === 'today' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('today', VIEW_TASKS_LIST)}
          >
            Today
          </div>
          <div
            className={status === 'tomorrow' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('tomorrow', VIEW_TASKS_LIST)}
          >
            Tomorrow
          </div>
          <div
            className={status === 'this_week' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('this_week', VIEW_TASKS_LIST)}
          >
            This Week
          </div>
          { currentUser.marketingListingsAccess ?
            <div
              className={status === 'project' ? activeButton : inactiveButton}
              onClick={() => this.handleStatusSelect('project', VIEW_TASKS_PROJECTS)}
            >
              By Project
            </div>
            : null
          }
          <div
            className={status === 'overdue' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('overdue', VIEW_TASKS_LIST)}
          >
            Overdue
          </div>
          <div
            className={status === 'completed' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('completed', VIEW_TASKS_LIST)}
          >
            Completed
          </div>
          <div
            className={status === 'all' ? activeButton : inactiveButton}
            onClick={() => this.handleStatusSelect('all', VIEW_TASKS_LIST)}
          >
            All
          </div>
        </div>
      </FilterSidebar>
    );
  }

  renderPageHeader() {
    return (
      <PageHeader>
        <PageHeader.Heading
          description="Quickly organize and run through your queued Tasks"
          icon={faCheck}
          title="Tasks"
        />
        <PageHeader.Actions>
          {this.renderCSVButton()}
          <a
            className="btn btn-primary"
            onClick={this.handleTaskFormOpen}
          >
            + New Task
          </a>
        </PageHeader.Actions>
      </PageHeader>
    );
  }

  renderActiveView() {
    const {
      activeView,
      availableDatabases,
      callListsAccess,
      currentUser,
      databaseMembers,
      hasNextPage,
      selectedId,
      tasks,
      onDeleteTask,
      onNextPage,
      onToggleTaskState,
      onUpdateTask
    } = this.props;

    const tableClass = classNames({
      'table': true,
      'table-responsive': true,
      'contacts-tasks': tasks.length > 0
    });

    switch (activeView) {
    case VIEW_TASKS_LIST:
      return (
        <div className="table-container">
          <table className={tableClass} name="tasks-list-view">
            <tbody>
              <TaskList
                availableDatabases={availableDatabases}
                callListsAccess={callListsAccess}
                databaseMembers={databaseMembers}
                hideByProjectSelect={!currentUser.marketingListingsAccess}
                hideCallListSelect={!currentUser.callListsEnabled}
                hideContactSelect={!currentUser.taskAssociationsEnabled}
                hideProjectSelect={!currentUser.marketingListingsAccess}
                hidePropertySelect={!currentUser.taskAssociationsEnabled}
                selectedId={selectedId}
                showLoadMoreLink={hasNextPage}
                tasks={tasks}

                onDeleteTask={onDeleteTask}
                onLoadMoreTasks={onNextPage}
                onToggleTaskState={onToggleTaskState}
                onUpdateTask={onUpdateTask}
              />
            </tbody>
          </table>
        </div>
      );
    case VIEW_TASKS_PROJECTS:
      if (currentUser.marketingListingsAccess)
        return (
          <ProjectsView
            databaseMembers={databaseMembers}
            name="projects-view"
            selectedId={selectedId}
          />
        );
      else {
        window.location.href = '/contacts/tasks';
      }
    }
  }

  renderTaskForm() {
    const { availableDatabases, currentUser, databaseMembers } = this.props;

    return (
      <TaskForm
        assigneeOptions={snakecaseKeys(databaseMembers)}
        availableDatabaseOptions={availableDatabases}
        defaultTaskAssignee={snakecaseKeys(databaseMembers.find(member => member.id === currentUser.id))}
        errors={this.state.taskFormErrors}
        hideByProjectSelect={!currentUser.marketingListingsAccess}
        hideCallListSelect={!currentUser.callListsEnabled}
        hideContactSelect={!currentUser.taskAssociationsEnabled}
        hideProjectSelect={!currentUser.marketingListingsAccess}
        hidePropertySelect={!currentUser.taskAssociationsEnabled}
        onCancel={this.handleTaskFormClose}
        onSave={this.handleTaskSave}
      />
    );
  }

  renderCreateTaskModal() {
    return (
      <Modal
        backdrop="static"
        centered
        className="contact-modal overflow-visible"
        name="task-form-modal"
        show={this.state.showTaskForm}
        size="lg"
        onHide={this.handleTaskFormClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create A Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderTaskForm()}
        </Modal.Body>
      </Modal>
    );
  }

  renderMobileTaskForm() {
    const { availableDatabases, callListsAccess, currentUser, databaseMembers } = this.props;
    const { showTaskForm } = this.state;

    if (showTaskForm) {
      return (
        <div className="tasks-index-mobile-content tasks-mobile-content">
          <MobileTaskForm
            assigneeOptions={snakecaseKeys(databaseMembers)}
            availableDatabaseOptions={availableDatabases}
            callListsAccess={callListsAccess}
            defaultTaskAssignee={snakecaseKeys(databaseMembers.find(member => member.id === currentUser.id))}
            errors={this.state.taskFormErrors}
            hideCallListSelect={!currentUser.callListsEnabled}
            multipleProjects={true}
            onCancel={this.handleTaskFormClose}
            onSave={this.handleTaskSave}
          />
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  activeView: state.ui.activeView,
  availableDatabases: state.static.availableDatabases,
  callListsAccess: state.static.callListsAccess,
  currentUser: state.static.currentUser,
  databaseMembers: state.static.databaseMembers,
  hasNextPage: state.search.hasNextPage,
  isFetching: state.search.isFetching,
  status: state.query.status,
  tasks: state.search.tasks
});

const mapDispatchToProps = {
  addTask,
  retrieveTasks,
  downloadTasksAsCsv,

  onChangeStatus: changeStatusFilter,
  onViewChange: changeTasksView,
  onDeleteTask: deleteTask,
  onNextPage: nextTaskPage,
  onToggleTaskState: toggleTaskState,
  onUpdateTask: updateTask
};

export default connect(mapStateToProps, mapDispatchToProps)(Tasks);
